// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/version/api-contract.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/version/src/api-contract.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo meal-planner-app
//     node fleet/fleet.ts check --repo meal-planner-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * API contract version — the client half.
 *
 * `APP_VERSION` ("0.2.0") is the product semver: it is for humans, it moves for
 * copy changes, and it is useless for deciding whether two builds can talk. The
 * contract version is a separate monotonic integer that moves ONLY when the
 * request/response shape changes in a way that matters. Both sides read it from
 * the same file — `api-contract.json` at the repo root — so within a commit the
 * front and the server cannot disagree about what the number is; they can only
 * disagree about which commit is deployed, which is exactly what we detect.
 *
 * The server publishes its window on `GET /api/system/health` as
 * `{"api": {"current": <int>, "min_supported": <int>}}`.
 */

/** The window of contract versions the running server speaks. */
export interface ApiContractWindow {
  /** Newest contract version the server speaks. */
  current: number;
  /** Oldest contract version the server still accepts. */
  minSupported: number;
}

export type ApiContractVerdict =
  /**
   * Nothing to decide: either the server does not publish a window (it predates
   * this feature) or this build does not know its own contract version. NEVER
   * an error — an unknown verdict must leave the app exactly as it was.
   */
  | { kind: "unknown" }
  /** Client version sits inside the server's window. */
  | { kind: "compatible" }
  /**
   * Client is BELOW `min_supported`: the server no longer speaks its dialect.
   * This is the only case that warrants remediation, and the remediation is the
   * client updating itself.
   */
  | { kind: "client-outdated"; clientVersion: number; minSupported: number }
  /**
   * Client is ABOVE `current`: the front was published before the API rollout
   * finished. This is the NORMAL state during a deploy and is transitory by
   * definition, so it must not block anything.
   */
  | { kind: "client-ahead"; clientVersion: number; serverCurrent: number };

/**
 * Type predicate rather than a cast: the payload comes off the wire, so nothing
 * about its shape is known, and a cast would only be a lie the compiler accepts.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readInteger(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0 ? value : null;
}

/**
 * Reads the contract window out of an arbitrary health payload.
 *
 * Deliberately defensive rather than typed-through: a server that predates this
 * feature simply omits `api`, and a server mid-rollout may return either shape.
 * Anything that is not a well-formed window reads as "no window", which the
 * evaluator turns into `unknown` — never into an error the user can see.
 */
export function readApiContractWindow(payload: unknown): ApiContractWindow | null {
  if (!isRecord(payload) || !isRecord(payload.api)) {
    return null;
  }
  const current = readInteger(payload.api, "current");
  const minSupported = readInteger(payload.api, "min_supported");
  if (current === null || minSupported === null) {
    return null;
  }
  // An inverted window is a server misconfiguration. Trusting it would mark
  // every client outdated at once — the exact global reload storm this whole
  // module exists to prevent. Refuse to act on it.
  if (minSupported > current) {
    return null;
  }
  return { current, minSupported };
}

/**
 * Compares this build's contract version against the server's window.
 *
 * Pure and total: every unusable input collapses to `unknown`. There is no
 * "throw" path, because a compatibility check that can fail loudly would turn a
 * transient deploy window into a visible outage.
 */
export function evaluateApiContract(
  clientVersion: number | null | undefined,
  window: ApiContractWindow | null,
): ApiContractVerdict {
  if (
    window === null ||
    typeof clientVersion !== "number" ||
    !Number.isSafeInteger(clientVersion) ||
    clientVersion <= 0
  ) {
    return { kind: "unknown" };
  }
  if (clientVersion < window.minSupported) {
    return { kind: "client-outdated", clientVersion, minSupported: window.minSupported };
  }
  if (clientVersion > window.current) {
    return { kind: "client-ahead", clientVersion, serverCurrent: window.current };
  }
  return { kind: "compatible" };
}
